import {
  type CreateSiteDraftInput,
  type SiteDocument,
  type SitePage,
  type SitePublication,
  type SitePublicationPublicConfig,
  type SiteRevision,
  type SiteSection,
  type SiteSummary,
  type SiteThemeBorderEmphasis,
  type SiteThemeCardRadius,
  type SiteThemeCardShadow,
  type SiteThemeSpacingScale,
  type SiteThemeTokens,
  siteDocumentSchema,
  sitePageSchema,
  sitePublicationPublicConfigSchema,
  sitePagePathSchema,
  sitePublicationSchema,
  siteRevisionSchema,
  siteSectionSchema,
  siteSummarySchema,
} from '../domains/site/schema';
import {
  type CreateSiteChangeProposalRequest,
  type SiteChangeProposal,
  type SiteDocumentDiff,
  type SiteDocumentDiffEntry,
  siteChangeProposalSchema,
} from '../domains/site-edit/schema';
import { getFormSettings } from './form-settings';
import { type TryDraftInput } from '../domains/try-session/schema';
import { apiRequest } from './api-client';
import { getTagSettings } from './tag-settings';
import { syncDocumentPipelineContext } from './pipeline-context-sync';
import { supportsSectionImage } from './section-images';
import { ensureSitePageHasFooterSection } from './site-footer-section';
import {
  decreaseBorderEmphasis,
  decreaseCardRadius,
  decreaseCardShadow,
  decreaseSpacingScale,
  getThemePresentation,
  increaseBorderEmphasis,
  increaseCardRadius,
  increaseCardShadow,
  increaseSpacingScale,
} from './theme-presentation';
import {
  buildWorkspacePersistenceSnapshot,
  isWorkspaceStorageUnavailableError,
  persistSiteWorkspaceSnapshot,
} from './site-workspaces';
import {
  type SiteWorkspace,
  siteWorkspaceSchema,
} from '../domains/site-workspace/schema';

type SiteDocumentRow = {
  site_id: string;
  version: number;
  document: unknown;
  updated_at?: string;
};

type SiteRevisionRow = {
  id: string;
  site_id: string;
  version: number;
  workspace_version?: number | null;
  summary: string;
  created_at: string;
  document?: unknown;
  workspace?: unknown;
};

type SitePublicationRow = {
  site_id: string;
  version: number;
  document: unknown;
  workspace_version?: number | null;
  workspace?: unknown;
  public_config?: unknown;
  published_at: string;
  updated_at: string;
};

type SaveSiteDocumentRevisionOptions = {
  workspace?: SiteWorkspace | null;
};

export type PublishedSiteSnapshot = {
  publication: SitePublication;
  workspace: SiteWorkspace | null;
};

type AddPageInput = {
  title: string;
  path: string;
};

const neutralTokens: SiteThemeTokens = {
  text: '#202123',
  textMuted: '#6e6e80',
  background: '#ffffff',
  surface: '#f7f7f8',
  surfaceMuted: '#f1f1f2',
  border: '#e5e7eb',
  primary: '#202123',
  secondary: '#2f2f2f',
};

const presetTokens: Record<string, SiteThemeTokens> = {
  信頼: neutralTokens,
  trust: neutralTokens,
  シンプル: {
    ...neutralTokens,
    surface: '#fafafa',
    surfaceMuted: '#f3f4f6',
  },
  simple: {
    ...neutralTokens,
    surface: '#fafafa',
    surfaceMuted: '#f3f4f6',
  },
  ポップ: {
    ...neutralTokens,
    primary: '#1f2937',
    secondary: '#4b5563',
    surface: '#f9fafb',
    border: '#d1d5db',
  },
  高級感: {
    ...neutralTokens,
    primary: '#1f1f1f',
    secondary: '#3f3f46',
    surface: '#f5f5f4',
    border: '#d6d3d1',
  },
  先進的: {
    ...neutralTokens,
    primary: '#111827',
    secondary: '#334155',
    surface: '#f8fafc',
    border: '#cbd5e1',
  },
  フォーマル: {
    ...neutralTokens,
    secondary: '#374151',
    surface: '#f8f8f8',
  },
  やわらかめ: {
    ...neutralTokens,
    surface: '#fbfbfb',
    surfaceMuted: '#f5f5f5',
  },
};

const siteSectionTypeLabels: Record<SiteSection['type'], string> = {
  hero: 'Hero',
  'feature-list': '内容紹介',
  problem: '課題',
  solution: '解決策',
  benefit: 'ベネフィット',
  feature_list: '特長一覧',
  reasons: '選ばれる理由',
  use_cases: '活用例',
  comparison: '比較',
  people: '人・体制',
  message: 'メッセージ',
  target_audience: '対象者',
  overview: '概要',
  speaker: '登壇者',
  instructor: '講師',
  work_style: '働き方',
  course_or_curriculum: 'コース・カリキュラム',
  offer: 'オファー',
  access: 'アクセス',
  services: '提供内容',
  features: '特徴・強み',
  proof: '実績・根拠',
  review: 'お客様の声',
  pricing: '料金',
  steps: '流れ',
  faq: 'FAQ',
  cta: 'CTA',
  footer: 'フッター',
};

const cardShadowLabels: Record<SiteThemeCardShadow, string> = {
  none: 'なし',
  soft: 'やわらかい',
  medium: '標準',
  strong: '強め',
};

const borderEmphasisLabels: Record<SiteThemeBorderEmphasis, string> = {
  none: 'なし',
  soft: '弱め',
  normal: '標準',
  strong: '強め',
};

const cardRadiusLabels: Record<SiteThemeCardRadius, string> = {
  sharp: 'シャープ',
  soft: '控えめ',
  rounded: '標準',
  extra_rounded: '大きめ',
};

const spacingScaleLabels: Record<SiteThemeSpacingScale, string> = {
  compact: '詰め',
  normal: '標準',
  spacious: 'ゆったり',
};

const cardLikeSectionTypes = new Set<SiteSection['type']>([
  'feature-list',
  'problem',
  'solution',
  'benefit',
  'feature_list',
  'reasons',
  'use_cases',
  'comparison',
  'people',
  'message',
  'target_audience',
  'overview',
  'speaker',
  'instructor',
  'work_style',
  'course_or_curriculum',
  'offer',
  'access',
  'services',
  'features',
  'proof',
  'review',
  'pricing',
  'steps',
]);

function createId(prefix: string) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID().slice(0, 8)}`;
  }

  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function cloneDocument(document: SiteDocument): SiteDocument {
  return finalizeSiteDocument(JSON.parse(JSON.stringify(document)));
}

function cloneSection(section: SiteSection): SiteSection {
  return siteSectionSchema.parse(JSON.parse(JSON.stringify(section)));
}

function clonePage(page: SitePage): SitePage {
  return sitePageSchema.parse(JSON.parse(JSON.stringify(page)));
}

function cloneThemeTokens(tokens: SiteThemeTokens): SiteThemeTokens {
  return JSON.parse(JSON.stringify(tokens)) as SiteThemeTokens;
}

export function getThemeTokensForPreset(preset: string): SiteThemeTokens {
  return cloneThemeTokens(presetTokens[preset] ?? neutralTokens);
}

function buildTokens(preset: string): SiteThemeTokens {
  return getThemeTokensForPreset(preset);
}

function finalizeSiteDocument(document: SiteDocument): SiteDocument {
  const parsed = siteDocumentSchema.parse(document);
  const pages = parsed.pages.map((page) => ensureSitePageHasFooterSection(parsed, page));
  return siteDocumentSchema.parse({
    ...parsed,
    pages,
  });
}

function buildDefaultHomePage(siteName: string, goal: string, ctaLabel: string): SitePage {
  const meta = getGoalMeta(goal);
  return {
    id: 'home',
    title: 'トップ',
    path: '/',
    sections: [
      {
        id: 'hero',
        type: 'hero',
        eyebrow: meta.label,
        headline: `${siteName}の魅力を、わかりやすく伝えます。`,
        subhead: '構成・文章・デザインを整理した下書きから、安心して編集を始められます。',
        ctaLabel,
        imageUrl: '',
        imageAlt: '',
      },
      {
        id: 'features',
        type: 'feature-list',
        title: 'このサイトで伝えたいこと',
        items: [
          {
            id: 'feature-speed',
            title: '迷わず伝わる構成',
            body: '最初の数秒で価値が伝わる情報設計を意識しています。',
          },
          {
            id: 'feature-safe',
            title: '安全に改善できる',
            body: '提案を確認してから適用できる前提で編集フローを組みます。',
          },
          {
            id: 'feature-action',
            title: '行動につながる導線',
            body: '最後まで読み切ったあとに自然にCTAへ進めるよう整理します。',
          },
        ],
      },
      {
        id: 'steps',
        type: 'steps',
        title: '公開までの流れ',
        items: [
          {
            id: 'step-1',
            title: '1. 下書きを作成',
            body: '目的とトーンをもとに、最初の構成をすばやく用意します。',
          },
          {
            id: 'step-2',
            title: '2. 必要な箇所だけ調整',
            body: '文章やセクションを対話的に見直して精度を上げます。',
          },
          {
            id: 'step-3',
            title: '3. 公開前チェック',
            body: '公開設定を確認してから、本番ステータスへ切り替えます。',
          },
        ],
      },
      {
        id: 'cta',
        type: 'cta',
        headline: meta.ctaHeadline,
        body: '下書きをベースに、伝えたいことを少しずつ磨き込めます。',
        ctaLabel,
        imageUrl: '',
        imageAlt: '',
      },
    ],
  };
}

function buildFaqSection(): SiteSection {
  return {
    id: 'faq',
    type: 'faq',
    title: 'よくある質問',
    items: [
      {
        id: 'faq-1',
        question: 'どこから編集を始めればいいですか？',
        answer: 'まずは Hero と CTA を整え、その後で補足情報を追加すると全体がまとまりやすくなります。',
      },
      {
        id: 'faq-2',
        question: '公開前に何を確認すべきですか？',
        answer: 'CTA 文言、連絡先、リンク切れ、SP 表示、公開 slug の5点を最低限確認してください。',
      },
    ],
  };
}

type GoalMeta = {
  label: string;
  ctaLabel: string;
  ctaHeadline: string;
  descriptionLabel: string;
};

const GOAL_MAP: { keyword: string; meta: GoalMeta }[] = [
  { keyword: 'お問い合わせ', meta: { label: 'お問い合わせ', ctaLabel: 'お問い合わせはこちら', ctaHeadline: 'お気軽にお問い合わせください。', descriptionLabel: 'サービス紹介' } },
  { keyword: '予約', meta: { label: 'ご予約', ctaLabel: 'ご予約はこちら', ctaHeadline: 'ご予約を承っております。', descriptionLabel: 'ご予約案内' } },
  { keyword: '申し込み', meta: { label: 'お申し込み', ctaLabel: 'お申し込みはこちら', ctaHeadline: 'お申し込みはこちらから。', descriptionLabel: 'お申し込み案内' } },
  { keyword: '資料請求', meta: { label: '資料請求', ctaLabel: '資料請求はこちら', ctaHeadline: 'まずは資料をご覧ください。', descriptionLabel: '資料請求' } },
  { keyword: '来店', meta: { label: 'ご来店', ctaLabel: 'ご来店予約はこちら', ctaHeadline: 'ぜひ一度お越しください。', descriptionLabel: '店舗案内' } },
  { keyword: '電話', meta: { label: 'お電話相談', ctaLabel: 'お電話で相談する', ctaHeadline: 'まずはお電話でご相談ください。', descriptionLabel: 'ご相談案内' } },
  { keyword: 'LINE', meta: { label: 'LINE相談', ctaLabel: 'LINEで相談する', ctaHeadline: 'LINEからお気軽にご相談いただけます。', descriptionLabel: 'ご相談案内' } },
  { keyword: '応募', meta: { label: 'ご応募', ctaLabel: '応募する', ctaHeadline: 'ご応募をお待ちしております。', descriptionLabel: '採用情報' } },
  { keyword: '採用', meta: { label: 'ご応募', ctaLabel: '応募する', ctaHeadline: 'ご応募をお待ちしております。', descriptionLabel: '採用情報' } },
  { keyword: '購入', meta: { label: 'ご購入', ctaLabel: '購入する', ctaHeadline: 'お買い求めはこちらから。', descriptionLabel: '商品紹介' } },
  { keyword: 'イベント', meta: { label: 'イベント', ctaLabel: '参加する', ctaHeadline: 'ぜひご参加ください。', descriptionLabel: 'イベント案内' } },
];

const DEFAULT_GOAL_META: GoalMeta = {
  label: '',
  ctaLabel: 'お問い合わせはこちら',
  ctaHeadline: 'お気軽にお問い合わせください。',
  descriptionLabel: 'サービス紹介',
};

export function getGoalMeta(goal: string): GoalMeta {
  return GOAL_MAP.find((entry) => goal.includes(entry.keyword))?.meta ?? DEFAULT_GOAL_META;
}

export function goalToLabel(goal: string): string | null {
  const meta = GOAL_MAP.find((entry) => goal.includes(entry.keyword))?.meta;
  return meta?.label ?? null;
}

function buildCtaLabel(goal: string, input?: string) {
  if (input?.trim()) return input.trim();
  return getGoalMeta(goal).ctaLabel;
}

function buildPageId(path: string) {
  if (path === '/') return 'home';
  return path.replace(/^\/+/u, '').replace(/\/+/gu, '-');
}

function buildDefaultPageTitle(siteName: string) {
  return siteName;
}

function buildDefaultPageDescription(siteName: string, goal: string) {
  const meta = getGoalMeta(goal);
  return `${siteName}の${meta.descriptionLabel}ページです。必要な情報を迷わず確認できるよう、要点を整理して紹介します。`;
}

export function normalizeSitePagePath(input: string) {
  const trimmed = input.trim().toLowerCase();
  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const normalized = withLeadingSlash.replace(/[^a-z0-9/-]/gu, '-').replace(/-+/gu, '-');
  return sitePagePathSchema.parse(normalized);
}

export function buildFallbackSiteDocument(siteName = 'Untitled Site'): SiteDocument {
  const goal = 'サービス紹介';
  const ctaLabel = 'お問い合わせ';

  return finalizeSiteDocument({
    version: 1,
    siteName,
    metadata: {
      goal,
      tone: '信頼',
      companyName: '',
      ctaText: ctaLabel,
      referenceUrl: '',
      pageTitle: buildDefaultPageTitle(siteName),
      pageDescription: buildDefaultPageDescription(siteName, goal),
      ogImageUrl: '',
      faviconUrl: '',
    },
    theme: {
      fontFamily: 'Noto Sans JP',
      preset: '信頼',
      tokens: buildTokens('信頼'),
    },
    pages: [buildDefaultHomePage(siteName, goal, ctaLabel)],
  });
}

export function buildInitialSiteDocument(input: CreateSiteDraftInput): SiteDocument {
  const ctaLabel = buildCtaLabel(input.goal, input.ctaText);

  return finalizeSiteDocument({
    version: 1,
    siteName: input.siteName,
    metadata: {
      goal: input.goal,
      tone: input.tone,
      companyName: input.companyName?.trim() ?? '',
      ctaText: ctaLabel,
      referenceUrl: input.referenceUrl?.trim() ?? '',
      pageTitle: buildDefaultPageTitle(input.siteName),
      pageDescription: buildDefaultPageDescription(input.siteName, input.goal),
      ogImageUrl: '',
      faviconUrl: '',
    },
    theme: {
      fontFamily: 'Noto Sans JP',
      preset: input.tone,
      tokens: buildTokens(input.tone),
    },
    pages: [buildDefaultHomePage(input.siteName, input.goal, ctaLabel)],
  });
}

export function buildTrySiteDocument(input: TryDraftInput): SiteDocument {
  const siteName = 'Imported from Try';
  const ctaLabel = buildCtaLabel(input.goal);

  return finalizeSiteDocument({
    version: 1,
    siteName,
    metadata: {
      goal: input.goal,
      tone: input.tone,
      companyName: '',
      ctaText: ctaLabel,
      referenceUrl: '',
      pageTitle: buildDefaultPageTitle(siteName),
      pageDescription: buildDefaultPageDescription(siteName, input.goal),
      ogImageUrl: '',
      faviconUrl: '',
    },
    theme: {
      fontFamily: 'Noto Sans JP',
      preset: input.tone,
      tokens: buildTokens(input.tone),
    },
    pages: [
      {
        ...buildDefaultHomePage(siteName, input.goal, ctaLabel),
        sections: [
          {
            id: 'hero',
            type: 'hero',
            eyebrow: goalToLabel(input.goal) ?? '',
            headline: input.headline,
            subhead: `${input.tone} な印象で最初の下書きを作成しました。`,
            ctaLabel,
            imageUrl: '',
            imageAlt: '',
          },
          {
            id: 'cta',
            type: 'cta',
            headline: 'まずはプレビューから始めましょう。',
            body: 'ログイン後に保存・公開設定へ進めます。',
            ctaLabel,
            imageUrl: '',
            imageAlt: '',
          },
        ],
      },
    ],
  });
}

function parseSiteDocumentRow(row: SiteDocumentRow): SiteDocument {
  const document = siteDocumentSchema.parse(row.document);
  return finalizeSiteDocument({
    ...document,
    version: row.version,
    updatedAt: row.updated_at ?? document.updatedAt,
  });
}

function parseSitePublicationRow(row: SitePublicationRow): SitePublication {
  return sitePublicationSchema.parse({
    site_id: row.site_id,
    version: row.version,
    document: finalizeSiteDocument(siteDocumentSchema.parse(row.document)),
    workspace_version: row.workspace_version ?? null,
    public_config: row.public_config,
    published_at: row.published_at,
    updated_at: row.updated_at,
  });
}

function parseStoredWorkspaceSnapshot(value: unknown): SiteWorkspace | null {
  if (!value) {
    return null;
  }

  try {
    return siteWorkspaceSchema.parse(value);
  } catch {
    return null;
  }
}

function buildRevisionWorkspaceSnapshot(
  document: SiteDocument,
  workspace?: SiteWorkspace | null,
) {
  if (!workspace) {
    return {
      snapshot: null,
      version: null,
    };
  }

  const snapshot = buildWorkspacePersistenceSnapshot(workspace, document);
  return {
    snapshot,
    version: snapshot.version,
  };
}

async function persistDocumentRevisionWorkspace(
  siteId: string,
  document: SiteDocument,
  workspace?: SiteWorkspace | null,
) {
  const revisionWorkspace = buildRevisionWorkspaceSnapshot(document, workspace);

  if (!revisionWorkspace.snapshot) {
    return revisionWorkspace;
  }

  try {
    const persistedWorkspace = await persistSiteWorkspaceSnapshot(siteId, revisionWorkspace.snapshot);
    return {
      snapshot: persistedWorkspace,
      version: persistedWorkspace.version,
    };
  } catch (error) {
    if (isWorkspaceStorageUnavailableError(error)) {
      return revisionWorkspace;
    }
    throw error;
  }
}

async function buildSitePublicationPublicConfig(siteId: string): Promise<SitePublicationPublicConfig> {
  const [tagSettings, formSettings] = await Promise.all([getTagSettings(siteId), getFormSettings(siteId)]);

  return sitePublicationPublicConfigSchema.parse({
    tags: {
      gtmId: tagSettings.gtmId ?? '',
      ga4Id: tagSettings.ga4Id ?? '',
      metaPixelId: tagSettings.metaPixelId ?? '',
    },
    form: {
      enabled: formSettings.settings.enabled,
      successMessage: formSettings.settings.successMessage,
      privacyPolicyUrl: formSettings.settings.privacyPolicyUrl ?? '',
      rateLimitSeconds: formSettings.settings.rateLimitSeconds,
      fields: formSettings.settings.fields,
    },
  });
}

export async function getSiteDocument(siteId: string, fallbackSiteName?: string): Promise<SiteDocument> {
  const data = await apiRequest<SiteDocumentRow | null>(`/api/sites/${encodeURIComponent(siteId)}/document`);
  if (!data) return buildFallbackSiteDocument(fallbackSiteName);

  return parseSiteDocumentRow(data);
}

export async function listSiteRevisions(
  siteId: string,
  options?: { maxVersions?: number; maxDays?: number },
): Promise<SiteRevision[]> {
  const search = new URLSearchParams();
  if (options?.maxDays && options.maxDays > 0) search.set('maxDays', String(options.maxDays));
  if (options?.maxVersions && options.maxVersions > 0) search.set('maxVersions', String(options.maxVersions));
  const suffix = search.size > 0 ? `?${search.toString()}` : '';
  const data = await apiRequest<unknown[]>(`/api/sites/${encodeURIComponent(siteId)}/revisions${suffix}`);
  return data.map((row) => siteRevisionSchema.parse(row));
}

export async function getPublishedSitePublication(siteId: string): Promise<SitePublication | null> {
  const data = await apiRequest<SitePublicationRow | null>(
    `/api/sites/${encodeURIComponent(siteId)}/publication`,
  );
  if (!data) return null;

  return parseSitePublicationRow(data);
}

export async function getPublishedSiteSnapshot(siteId: string): Promise<PublishedSiteSnapshot | null> {
  const data = await apiRequest<SitePublicationRow | null>(
    `/api/sites/${encodeURIComponent(siteId)}/publication`,
  );
  if (!data) return null;

  return {
    publication: parseSitePublicationRow(data),
    workspace: parseStoredWorkspaceSnapshot(data.workspace),
  };
}

export async function publishSiteSnapshot(
  siteId: string,
  document: SiteDocument,
  options: SaveSiteDocumentRevisionOptions = {},
): Promise<{ publication: SitePublication; site: SiteSummary }> {
  const validated = finalizeSiteDocument(document);
  const publicConfig = await buildSitePublicationPublicConfig(siteId);
  const workspaceSnapshot = await persistDocumentRevisionWorkspace(siteId, validated, options.workspace);
  const data = await apiRequest<{ publication: SitePublicationRow; site: unknown }>(
    `/api/sites/${encodeURIComponent(siteId)}/publication`,
    {
      method: 'PUT',
      body: {
        document: validated,
        publicConfig,
        workspace: workspaceSnapshot.snapshot,
      },
    },
  );

  return {
    publication: parseSitePublicationRow(data.publication),
    site: siteSummarySchema.parse(data.site),
  };
}

export async function saveSiteDocumentRevision(
  siteId: string,
  document: SiteDocument,
  summary: string,
  options: SaveSiteDocumentRevisionOptions = {},
): Promise<{ document: SiteDocument; revision: SiteRevision; site: SiteSummary; workspace: SiteWorkspace | null }> {
  const synced = syncDocumentPipelineContext(document);
  const validated = finalizeSiteDocument(synced);
  const existingRow = await apiRequest<SiteDocumentRow | null>(
    `/api/sites/${encodeURIComponent(siteId)}/document`,
  );

  const nextVersion = Number(existingRow?.version ?? 0) + 1;
  const now = new Date().toISOString();
  const documentToPersist = finalizeSiteDocument({
    ...validated,
    version: nextVersion,
    updatedAt: now,
  });
  const workspaceSnapshot = await persistDocumentRevisionWorkspace(siteId, documentToPersist, options.workspace);
  const result = await apiRequest<{
    document: unknown;
    revision: unknown;
    site: unknown;
    workspace: unknown;
  }>(`/api/sites/${encodeURIComponent(siteId)}/revisions`, {
    method: 'POST',
    body: {
      document: documentToPersist,
      summary,
      workspace: workspaceSnapshot.snapshot,
    },
  });

  return {
    document: finalizeSiteDocument(siteDocumentSchema.parse(result.document)),
    revision: siteRevisionSchema.parse(result.revision),
    site: siteSummarySchema.parse(result.site),
    workspace: parseStoredWorkspaceSnapshot(result.workspace),
  };
}

export async function restoreSiteDocumentRevision(
  siteId: string,
  revisionId: string,
): Promise<{ document: SiteDocument; revision: SiteRevision; site: SiteSummary; workspace: SiteWorkspace | null }> {
  const row = await apiRequest<SiteRevisionRow>(
    `/api/sites/${encodeURIComponent(siteId)}/revisions/${encodeURIComponent(revisionId)}`,
  );
  const revisionDocument = finalizeSiteDocument(siteDocumentSchema.parse(row.document));
  const revisionWorkspace = parseStoredWorkspaceSnapshot(row.workspace);
  return saveSiteDocumentRevision(siteId, revisionDocument, `復元: ${row.summary}`, {
    workspace: revisionWorkspace,
  });
}

export function addPageToSiteDocument(document: SiteDocument, input: AddPageInput): SiteDocument {
  const title = input.title.trim();
  if (!title) {
    throw new Error('ページ名を入力してください。');
  }

  const path = normalizeSitePagePath(input.path);
  const nextDocument = cloneDocument(document);

  if (nextDocument.pages.some((page) => page.path === path)) {
    throw new Error('同じ path のページがすでに存在します。');
  }

  nextDocument.pages.push({
    id: buildPageId(path),
    title,
    path,
    sections: [
      {
        id: createId('hero'),
        type: 'hero',
        eyebrow: goalToLabel(nextDocument.metadata.goal) ?? '',
        headline: `${title}の内容をここで伝えます。`,
        subhead: `${title}ページの要点を短く整理してください。`,
        ctaLabel: nextDocument.metadata.ctaText || 'お問い合わせ',
        imageUrl: '',
        imageAlt: '',
      },
      {
        id: createId('cta'),
        type: 'cta',
        headline: `${title}について、さらに詳しくご案内します。`,
        body: '必要な情報だけを載せて、読み手が迷わない導線を保ちます。',
        ctaLabel: nextDocument.metadata.ctaText || 'お問い合わせ',
        imageUrl: '',
        imageAlt: '',
      },
    ],
  });

  return finalizeSiteDocument(nextDocument);
}

export function applySectionProposalToSiteDocument(
  document: SiteDocument,
  proposalDocument: SiteDocument,
  sectionId: string,
): SiteDocument {
  const nextDocument = cloneDocument(document);
  const proposalPage = proposalDocument.pages.find((page) => page.sections.some((section) => section.id === sectionId));

  if (!proposalPage) {
    throw new Error('適用対象のセクションが提案内に見つかりません。');
  }

  const proposalSection = proposalPage.sections.find((section) => section.id === sectionId);
  if (!proposalSection) {
    throw new Error('適用対象のセクションが提案内に見つかりません。');
  }

  const pageIndex = nextDocument.pages.findIndex((page) => page.id === proposalPage.id);
  if (pageIndex === -1) {
    nextDocument.pages.push({
      ...proposalPage,
      sections: [cloneSection(proposalSection)],
    });

    return finalizeSiteDocument(nextDocument);
  }

  const nextPage = nextDocument.pages[pageIndex];
  const sectionIndex = nextPage.sections.findIndex((section) => section.id === sectionId);
  if (sectionIndex >= 0) {
    nextPage.sections[sectionIndex] = cloneSection(proposalSection);
  } else {
    const proposalSectionIndex = proposalPage.sections.findIndex((section) => section.id === sectionId);
    nextPage.sections.splice(Math.min(proposalSectionIndex, nextPage.sections.length), 0, cloneSection(proposalSection));
  }

  nextDocument.pages[pageIndex] = nextPage;
  return finalizeSiteDocument(nextDocument);
}

export function applyPageProposalToSiteDocument(
  document: SiteDocument,
  proposalDocument: SiteDocument,
  pageId: string,
): SiteDocument {
  const nextDocument = cloneDocument(document);
  const proposalPage = proposalDocument.pages.find((page) => page.id === pageId);

  if (!proposalPage) {
    throw new Error('適用対象のページが提案内に見つかりません。');
  }

  const pageIndex = nextDocument.pages.findIndex((page) => page.id === pageId);
  if (pageIndex >= 0) {
    nextDocument.pages[pageIndex] = clonePage(proposalPage);
    return finalizeSiteDocument(nextDocument);
  }

  const proposalPageIndex = proposalDocument.pages.findIndex((page) => page.id === pageId);
  nextDocument.pages.splice(Math.min(proposalPageIndex, nextDocument.pages.length), 0, clonePage(proposalPage));

  return finalizeSiteDocument(nextDocument);
}

export function applyAssetToSiteMetadata(
  document: SiteDocument,
  target: 'ogImageUrl' | 'faviconUrl',
  publicUrl: string,
): SiteDocument {
  const nextDocument = cloneDocument(document);
  nextDocument.metadata[target] = publicUrl.trim();
  return finalizeSiteDocument(nextDocument);
}

export function applyAssetToSectionImage(
  document: SiteDocument,
  sectionId: string,
  publicUrl: string,
  imageAlt = '',
  imageAssetId = '',
): SiteDocument {
  const nextDocument = cloneDocument(document);
  const page = nextDocument.pages.find((item) => item.sections.some((section) => section.id === sectionId));

  if (!page) {
    throw new Error('反映先のセクションが見つかりません。');
  }

  const section = page.sections.find((item) => item.id === sectionId);
  if (!section) {
    throw new Error('反映先のセクションが見つかりません。');
  }

  if (!supportsSectionImage(section)) {
    throw new Error('このセクションには画像を反映できません。');
  }

  section.imageUrl = publicUrl.trim();
  section.imageAlt = imageAlt.trim();
  section.imageAssetId = imageAssetId.trim();

  return finalizeSiteDocument(nextDocument);
}

function getCurrentPage(document: SiteDocument, currentPageId: string) {
  return document.pages.find((page) => page.id === currentPageId) ?? document.pages[0];
}

function getHeroSection(page: SitePage) {
  return page.sections.find((section) => section.type === 'hero');
}

function getCtaSection(page: SitePage) {
  return page.sections.find((section) => section.type === 'cta');
}

function getFaqSection(page: SitePage) {
  return page.sections.find((section) => section.type === 'faq');
}

function getSectionById(page: SitePage, sectionId?: string | null) {
  if (!sectionId) return undefined;
  return page.sections.find((section) => section.id === sectionId);
}

function getSectionLabel(section: SiteSection) {
  return siteSectionTypeLabels[section.type];
}

function isCardShadowPrompt(normalizedPrompt: string, selectedSection?: SiteSection) {
  if (!/(shadow|シャドウ|影|立体感|浮か)/iu.test(normalizedPrompt)) {
    return false;
  }

  if (/(card|カード|パネル|panel|ボックス|box)/iu.test(normalizedPrompt)) {
    return true;
  }

  return Boolean(selectedSection && cardLikeSectionTypes.has(selectedSection.type));
}

function isBorderEmphasisPrompt(normalizedPrompt: string, selectedSection?: SiteSection) {
  if (!/(border|ボーダー|アウトライン|枠線|枠|縁|境界線|境界|輪郭)/iu.test(normalizedPrompt)) {
    return false;
  }

  if (/(card|カード|パネル|panel|ボックス|box)/iu.test(normalizedPrompt)) {
    return true;
  }

  return Boolean(selectedSection && cardLikeSectionTypes.has(selectedSection.type));
}

function isCardRadiusPrompt(normalizedPrompt: string, selectedSection?: SiteSection) {
  if (!/(角丸|角を丸|corner|corners|radius|丸め|丸く|シャープ|角ば|角を立)/iu.test(normalizedPrompt)) {
    return false;
  }

  if (/(card|カード|パネル|panel|ボックス|box|コンテナ|container)/iu.test(normalizedPrompt)) {
    return true;
  }

  return Boolean(selectedSection && cardLikeSectionTypes.has(selectedSection.type));
}

function isSpacingScalePrompt(normalizedPrompt: string, selectedSection?: SiteSection) {
  if (!/(余白|間隔|spacing|space|詰め|ゆったり|密度|スカスカ|窮屈|padding|gap)/iu.test(normalizedPrompt)) {
    return false;
  }

  if (/(card|カード|パネル|panel|ボックス|box|コンテナ|container|セクション|section)/iu.test(normalizedPrompt)) {
    return true;
  }

  if (!selectedSection && /(全体|サイト|ページ|レイアウト|見た目|デザイン)/iu.test(normalizedPrompt)) {
    return true;
  }

  return Boolean(selectedSection && cardLikeSectionTypes.has(selectedSection.type));
}

function getNextCardShadow(
  normalizedPrompt: string,
  currentCardShadow: SiteThemeCardShadow,
): SiteThemeCardShadow {
  if (/(なく|無し|なし|消|外|取って|やめ)/iu.test(normalizedPrompt)) {
    return 'none';
  }

  if (/(弱|薄|控えめ|やわらか|柔らか|soft|ソフト)/iu.test(normalizedPrompt)) {
    return decreaseCardShadow(currentCardShadow);
  }

  if (/(もっと|もう少し|さらに|より|強|濃|深|しっかり|くっきり)/iu.test(normalizedPrompt)) {
    return increaseCardShadow(currentCardShadow);
  }

  return currentCardShadow === 'none' ? 'medium' : currentCardShadow;
}

function getNextBorderEmphasis(
  normalizedPrompt: string,
  currentBorderEmphasis: SiteThemeBorderEmphasis,
): SiteThemeBorderEmphasis {
  if (/(なく|無し|なし|消|外|取って|やめ)/iu.test(normalizedPrompt)) {
    return 'none';
  }

  if (/(弱|薄|控えめ|軽|soft|ソフト)/iu.test(normalizedPrompt)) {
    return decreaseBorderEmphasis(currentBorderEmphasis);
  }

  if (/(もっと|もう少し|さらに|より|強|濃|はっきり|くっきり|太)/iu.test(normalizedPrompt)) {
    return increaseBorderEmphasis(currentBorderEmphasis);
  }

  return currentBorderEmphasis === 'none' ? 'normal' : currentBorderEmphasis;
}

function getNextCardRadius(
  normalizedPrompt: string,
  currentCardRadius: SiteThemeCardRadius,
): SiteThemeCardRadius {
  if (/(なく|無し|なし|消|外|取って|やめ|四角|スクエア|square)/iu.test(normalizedPrompt)) {
    return 'sharp';
  }

  if (/(控えめ|弱|小さ|シャープ|角ば|角を立)/iu.test(normalizedPrompt)) {
    return decreaseCardRadius(currentCardRadius);
  }

  if (/(もっと|もう少し|さらに|より|丸く|丸め|やわらか|柔らか)/iu.test(normalizedPrompt)) {
    return increaseCardRadius(currentCardRadius);
  }

  return currentCardRadius === 'sharp' ? 'rounded' : currentCardRadius;
}

function getNextSpacingScale(
  normalizedPrompt: string,
  currentSpacingScale: SiteThemeSpacingScale,
): SiteThemeSpacingScale {
  if (/(詰め|狭|密|コンパクト|compact|tight|窮屈)/iu.test(normalizedPrompt)) {
    return decreaseSpacingScale(currentSpacingScale);
  }

  if (/(広|増や|ゆったり|余裕|spacious|loose|air|空気感)/iu.test(normalizedPrompt)) {
    return increaseSpacingScale(currentSpacingScale);
  }

  return currentSpacingScale === 'compact' ? 'normal' : currentSpacingScale;
}

function applyPromptToSelectedSection(
  section: SiteSection,
  normalizedPrompt: string,
  pageId: string,
  diff: SiteDocumentDiff,
) {
  if (section.type === 'hero') {
    const previousSubhead = section.subhead;
    section.subhead = `${normalizedPrompt} を踏まえて、導入文をよりわかりやすく整えました。`;
    diff.text.push(
      createDiffEntry({
        label: 'Hero 補足文',
        action: 'updated',
        scope: 'section',
        pageId,
        sectionId: section.id,
        before: previousSubhead,
        after: section.subhead,
      }),
    );
    return '選択中の Hero を中心に、導入文を調整しました。';
  }

  if (section.type === 'cta') {
    const previousBody = section.body;
    section.body = `${normalizedPrompt} を踏まえて、最後の行動につながる説明へ整えました。`;
    diff.text.push(
      createDiffEntry({
        label: 'CTA 説明文',
        action: 'updated',
        scope: 'section',
        pageId,
        sectionId: section.id,
        before: previousBody,
        after: section.body,
      }),
    );
    return '選択中の CTA を中心に、行動導線の文言を調整しました。';
  }

  if (section.type === 'faq') {
    const beforeCount = section.items.length;
    section.items.push({
      id: createId('faq'),
      question: '事前に確認しておきたいことはありますか？',
      answer: `${normalizedPrompt} を踏まえて、申し込み前の不安を解消する情報を追記しました。`,
    });
    diff.text.push(
      createDiffEntry({
        label: 'FAQ 項目',
        action: 'updated',
        scope: 'section',
        pageId,
        sectionId: section.id,
        before: `質問数 ${beforeCount} 件`,
        after: `質問数 ${section.items.length} 件`,
      }),
    );
    return '選択中の FAQ に質問を追加し、不安を解消しやすい形に整えました。';
  }

  if (
    section.type === 'feature-list' ||
    section.type === 'problem' ||
    section.type === 'solution' ||
    section.type === 'benefit' ||
    section.type === 'feature_list' ||
    section.type === 'reasons' ||
    section.type === 'use_cases' ||
    section.type === 'comparison' ||
    section.type === 'people' ||
    section.type === 'message' ||
    section.type === 'target_audience' ||
    section.type === 'overview' ||
    section.type === 'speaker' ||
    section.type === 'instructor' ||
    section.type === 'work_style' ||
    section.type === 'course_or_curriculum' ||
    section.type === 'offer' ||
    section.type === 'access' ||
    section.type === 'services' ||
    section.type === 'features'
  ) {
    const previousBody = section.items[0]?.body ?? '';
    section.items[0].body = `${normalizedPrompt} を踏まえて、要点がひと目で伝わる説明に整えました。`;
    diff.text.push(
      createDiffEntry({
        label: `${getSectionLabel(section)} 先頭項目`,
        action: 'updated',
        scope: 'section',
        pageId,
        sectionId: section.id,
        before: previousBody,
        after: section.items[0].body,
      }),
    );
    return `選択中の${getSectionLabel(section)}を中心に、説明文を調整しました。`;
  }

  if (section.type === 'steps') {
    const previousBody = section.items[0]?.body ?? '';
    section.items[0].body = `${normalizedPrompt} を踏まえて、最初の行動がわかる説明に整えました。`;
    diff.text.push(
      createDiffEntry({
        label: 'ステップ説明',
        action: 'updated',
        scope: 'section',
        pageId,
        sectionId: section.id,
        before: previousBody,
        after: section.items[0].body,
      }),
    );
    return '選択中の流れセクションを中心に、手順説明を調整しました。';
  }

  if (section.type === 'proof') {
    const previousLabel = section.items[0]?.label ?? '';
    if (section.items[0]) {
      section.items[0].label = '信頼の根拠が伝わる補足に整えました。';
      diff.text.push(
        createDiffEntry({
          label: '実績ラベル',
          action: 'updated',
          scope: 'section',
          pageId,
          sectionId: section.id,
          before: previousLabel,
          after: section.items[0].label,
        }),
      );
    }
    return '選択中の実績・根拠セクションを中心に、見せ方を整えました。';
  }

  if (section.type === 'pricing') {
    const previousBody = section.items[0]?.body ?? '';
    if (section.items[0]) {
      section.items[0].body = '比較しやすいよう、料金の考え方を補足しました。';
      diff.text.push(
        createDiffEntry({
          label: '料金補足',
          action: 'updated',
          scope: 'section',
          pageId,
          sectionId: section.id,
          before: previousBody,
          after: section.items[0].body,
        }),
      );
    }
    return '選択中の料金セクションを中心に、比較しやすい説明へ整えました。';
  }

  if (section.type === 'footer') {
    const previousDescription = section.description;
    section.description = `${normalizedPrompt} を踏まえて、フッターの説明を見直しました。`;
    diff.text.push(
      createDiffEntry({
        label: 'フッター説明',
        action: 'updated',
        scope: 'section',
        pageId,
        sectionId: section.id,
        before: previousDescription,
        after: section.description,
      }),
    );
    return '選択中のフッターを中心に、補足説明を調整しました。';
  }

  const previousTitle = section.title;
  section.title = `${getSectionLabel(section)}を見直しました`;
  diff.text.push(
    createDiffEntry({
      label: `${getSectionLabel(section)} タイトル`,
      action: 'updated',
      scope: 'section',
      pageId,
      sectionId: section.id,
      before: previousTitle,
      after: section.title,
    }),
  );
  return `選択中の${getSectionLabel(section)}を中心に見出しを調整しました。`;
}

function createDiffEntry({
  label,
  action,
  scope,
  pageId = '',
  sectionId = '',
  before = '',
  after = '',
}: {
  label: string;
  action: SiteDocumentDiffEntry['action'];
  scope: SiteDocumentDiffEntry['scope'];
  pageId?: string;
  sectionId?: string;
  before?: string;
  after?: string;
}): SiteDocumentDiffEntry {
  return {
    label,
    action,
    scope,
    pageId,
    sectionId,
    before,
    after,
  };
}

export function createSiteChangeProposal({
  document,
  prompt,
  currentPageId,
  scope,
  selectedSectionId,
}: CreateSiteChangeProposalRequest): SiteChangeProposal {
  const nextDocument = cloneDocument(document);
  const page = getCurrentPage(nextDocument, currentPageId);
  const normalizedPrompt = prompt.trim();
  const lowerPrompt = normalizedPrompt.toLowerCase();
  const diff: SiteDocumentDiff = {
    text: [],
    structure: [],
    theme: [],
  };
  const selectedSection = scope === 'section' ? getSectionById(page, selectedSectionId) : undefined;

  if (/(faq|よくある質問)/iu.test(lowerPrompt)) {
    if (!getFaqSection(page)) {
      page.sections.splice(Math.max(page.sections.length - 1, 1), 0, buildFaqSection());
      diff.structure.push(
        createDiffEntry({
          label: 'FAQ セクション',
          action: 'added',
          scope: 'page',
          pageId: page.id,
          sectionId: 'faq',
          after: 'ページ末尾に FAQ セクションを追加しました。',
        }),
      );
    } else {
      const faqSection = getFaqSection(page);
      if (faqSection?.type === 'faq') {
        const beforeCount = faqSection.items.length;
        faqSection.items.push({
          id: createId('faq'),
          question: 'どんな内容を追記すると良いですか？',
          answer: '申し込み前の不安を解消する情報を追加すると、CV につながりやすくなります。',
        });
        diff.text.push(
          createDiffEntry({
            label: 'FAQ 項目',
            action: 'updated',
            scope: 'section',
            pageId: page.id,
            sectionId: faqSection.id,
            before: `質問数 ${beforeCount} 件`,
            after: `質問数 ${faqSection.items.length} 件`,
          }),
        );
      }
    }

    return siteChangeProposalSchema.parse({
      nextDocument,
      summary: 'FAQ を追加して、不安を解消しやすい構成に整えました。',
      diff,
    });
  }

  if (/(会社概要|about)/iu.test(lowerPrompt)) {
    const hasAboutPage = nextDocument.pages.some((item) => item.path === '/about');
    if (!hasAboutPage) {
      nextDocument.pages.push({
        id: 'about',
        title: '会社概要',
        path: '/about',
        sections: [
          {
            id: 'about-hero',
            type: 'hero',
            eyebrow: 'Company',
            headline: `${nextDocument.siteName}について`,
            subhead: '事業内容・背景・価値観を整理して掲載できるページです。',
            ctaLabel: nextDocument.metadata.ctaText || 'お問い合わせ',
            imageUrl: '',
            imageAlt: '',
          },
          {
            id: 'about-features',
            type: 'feature-list',
            title: 'このページに載せたい情報',
            items: [
              {
                id: 'about-company',
                title: '事業内容',
                body: '誰に、どんな価値を、どのように届けているかを簡潔にまとめます。',
              },
              {
                id: 'about-values',
                title: '強み',
                body: '実績、チーム、サポート体制など、信頼につながる要素を並べます。',
              },
            ],
          },
          {
            id: 'about-cta',
            type: 'cta',
            headline: '詳しいご相談も受け付けています。',
            body: '読み手が次の行動へ進みやすいよう、最後に問い合わせ導線を置きます。',
            ctaLabel: nextDocument.metadata.ctaText || 'お問い合わせ',
            imageUrl: '',
            imageAlt: '',
          },
        ],
      });
      diff.structure.push(
        createDiffEntry({
          label: '会社概要ページ',
          action: 'added',
          scope: 'site',
          pageId: 'about',
          after: '/about の固定ページを追加しました。',
        }),
      );
    } else {
      diff.structure.push(
        createDiffEntry({
          label: '会社概要ページ',
          action: 'updated',
          scope: 'site',
          pageId: 'about',
          before: '既存ページあり',
          after: '既存の会社概要ページを維持しました。',
        }),
      );
    }

    return siteChangeProposalSchema.parse({
      nextDocument,
      summary: '会社概要ページを追加して、サイト全体の情報導線を広げました。',
      diff,
    });
  }

  if (isCardShadowPrompt(normalizedPrompt, selectedSection)) {
    const currentPresentation = getThemePresentation(nextDocument.theme);
    const nextCardShadow = getNextCardShadow(normalizedPrompt, currentPresentation.cardShadow);

    nextDocument.theme.presentation = {
      ...(nextDocument.theme.presentation ?? currentPresentation),
      cardShadow: nextCardShadow,
    };

    diff.theme.push(
      createDiffEntry({
        label: selectedSection ? `${getSectionLabel(selectedSection)} カードの影` : 'カードの影',
        action: 'updated',
        scope: selectedSection ? 'section' : 'site',
        pageId: selectedSection ? page.id : '',
        sectionId: selectedSection?.id ?? '',
        before: cardShadowLabels[currentPresentation.cardShadow],
        after: cardShadowLabels[nextCardShadow],
      }),
    );

    const summary = selectedSection
      ? `「${getSectionLabel(selectedSection)}」セクションのカード影を調整するため、テーマの影設定を${cardShadowLabels[nextCardShadow]}に更新しました。`
      : `カードの影を調整するため、テーマの影設定を${cardShadowLabels[nextCardShadow]}に更新しました。`;

    return siteChangeProposalSchema.parse({
      nextDocument,
      summary,
      diff,
    });
  }

  if (isBorderEmphasisPrompt(normalizedPrompt, selectedSection)) {
    const currentPresentation = getThemePresentation(nextDocument.theme);
    const nextBorderEmphasis = getNextBorderEmphasis(
      normalizedPrompt,
      currentPresentation.borderEmphasis,
    );

    nextDocument.theme.presentation = {
      ...(nextDocument.theme.presentation ?? currentPresentation),
      borderEmphasis: nextBorderEmphasis,
    };

    diff.theme.push(
      createDiffEntry({
        label: selectedSection ? `${getSectionLabel(selectedSection)} カードの枠線` : 'カードの枠線',
        action: 'updated',
        scope: selectedSection ? 'section' : 'site',
        pageId: selectedSection ? page.id : '',
        sectionId: selectedSection?.id ?? '',
        before: borderEmphasisLabels[currentPresentation.borderEmphasis],
        after: borderEmphasisLabels[nextBorderEmphasis],
      }),
    );

    const summary = selectedSection
      ? `「${getSectionLabel(selectedSection)}」セクションのカード境界を調整するため、テーマの枠線設定を${borderEmphasisLabels[nextBorderEmphasis]}に更新しました。`
      : `カード境界を調整するため、テーマの枠線設定を${borderEmphasisLabels[nextBorderEmphasis]}に更新しました。`;

    return siteChangeProposalSchema.parse({
      nextDocument,
      summary,
      diff,
    });
  }

  if (isCardRadiusPrompt(normalizedPrompt, selectedSection)) {
    const currentPresentation = getThemePresentation(nextDocument.theme);
    const nextCardRadius = getNextCardRadius(
      normalizedPrompt,
      currentPresentation.cardRadius,
    );

    nextDocument.theme.presentation = {
      ...(nextDocument.theme.presentation ?? currentPresentation),
      cardRadius: nextCardRadius,
    };

    diff.theme.push(
      createDiffEntry({
        label: selectedSection ? `${getSectionLabel(selectedSection)} カードの角丸` : 'カードの角丸',
        action: 'updated',
        scope: selectedSection ? 'section' : 'site',
        pageId: selectedSection ? page.id : '',
        sectionId: selectedSection?.id ?? '',
        before: cardRadiusLabels[currentPresentation.cardRadius],
        after: cardRadiusLabels[nextCardRadius],
      }),
    );

    const summary = selectedSection
      ? `「${getSectionLabel(selectedSection)}」セクションのカード角丸を調整するため、テーマの角丸設定を${cardRadiusLabels[nextCardRadius]}に更新しました。`
      : `カード角丸を調整するため、テーマの角丸設定を${cardRadiusLabels[nextCardRadius]}に更新しました。`;

    return siteChangeProposalSchema.parse({
      nextDocument,
      summary,
      diff,
    });
  }

  if (isSpacingScalePrompt(normalizedPrompt, selectedSection)) {
    const currentPresentation = getThemePresentation(nextDocument.theme);
    const nextSpacingScale = getNextSpacingScale(
      normalizedPrompt,
      currentPresentation.spacingScale,
    );

    nextDocument.theme.presentation = {
      ...(nextDocument.theme.presentation ?? currentPresentation),
      spacingScale: nextSpacingScale,
    };

    diff.theme.push(
      createDiffEntry({
        label: selectedSection ? `${getSectionLabel(selectedSection)} 余白密度` : '余白密度',
        action: 'updated',
        scope: selectedSection ? 'section' : 'site',
        pageId: selectedSection ? page.id : '',
        sectionId: selectedSection?.id ?? '',
        before: spacingScaleLabels[currentPresentation.spacingScale],
        after: spacingScaleLabels[nextSpacingScale],
      }),
    );

    const summary = selectedSection
      ? `「${getSectionLabel(selectedSection)}」セクションの余白密度を調整するため、テーマの余白設定を${spacingScaleLabels[nextSpacingScale]}に更新しました。`
      : `余白密度を調整するため、テーマの余白設定を${spacingScaleLabels[nextSpacingScale]}に更新しました。`;

    return siteChangeProposalSchema.parse({
      nextDocument,
      summary,
      diff,
    });
  }

  if (/(cta|お問い合わせ|応募|資料請求)/iu.test(lowerPrompt)) {
    const heroSection = getHeroSection(page);
    if (heroSection?.type === 'hero') {
      const previousSubhead = heroSection.subhead;
      heroSection.ctaLabel = buildCtaLabel(document.metadata.goal, document.metadata.ctaText || 'お問い合わせ');
      heroSection.subhead = '読み手が迷わず次の行動に進めるよう、訴求と導線を整理しました。';
      diff.text.push(
        createDiffEntry({
          label: 'Hero 補足文',
          action: 'updated',
          scope: 'section',
          pageId: page.id,
          sectionId: heroSection.id,
          before: previousSubhead,
          after: heroSection.subhead,
        }),
      );
    }

    const ctaSection = getCtaSection(page);
    if (ctaSection?.type === 'cta') {
      const previousHeadline = ctaSection.headline;
      const previousBody = ctaSection.body;
      ctaSection.headline = '行動につながる最後のひと押しを、ここで明確にします。';
      ctaSection.body = '問い合わせ・応募・資料請求のいずれでも迷わないよう、表現を統一しました。';
      ctaSection.ctaLabel = buildCtaLabel(document.metadata.goal, document.metadata.ctaText || 'お問い合わせ');
      diff.text.push(
        createDiffEntry({
          label: '最終 CTA 見出し',
          action: 'updated',
          scope: 'section',
          pageId: page.id,
          sectionId: ctaSection.id,
          before: previousHeadline,
          after: ctaSection.headline,
        }),
      );
      diff.text.push(
        createDiffEntry({
          label: '最終 CTA 説明文',
          action: 'updated',
          scope: 'section',
          pageId: page.id,
          sectionId: ctaSection.id,
          before: previousBody,
          after: ctaSection.body,
        }),
      );
    }

    return siteChangeProposalSchema.parse({
      nextDocument,
      summary: 'CTA を目立たせ、最後の行動導線が伝わりやすい表現に整えました。',
      diff,
    });
  }

  if (selectedSection) {
    const summary = applyPromptToSelectedSection(
      selectedSection,
      normalizedPrompt,
      page.id,
      diff,
    );

    return siteChangeProposalSchema.parse({
      nextDocument,
      summary,
      diff,
    });
  }

  if (/(シンプル|短く|簡潔|要約)/iu.test(lowerPrompt)) {
    const heroSection = getHeroSection(page);
    if (heroSection?.type === 'hero') {
      const previousSubhead = heroSection.subhead;
      heroSection.subhead = '必要な情報だけに絞って、短く読みやすい導入に整えました。';
      diff.text.push(
        createDiffEntry({
          label: 'Hero 補足文',
          action: 'updated',
          scope: 'section',
          pageId: page.id,
          sectionId: heroSection.id,
          before: previousSubhead,
          after: heroSection.subhead,
        }),
      );
    }

    const ctaSection = getCtaSection(page);
    if (ctaSection?.type === 'cta') {
      const previousBody = ctaSection.body;
      ctaSection.body = '迷わず次の行動へ進める、短い導線に整えました。';
      diff.text.push(
        createDiffEntry({
          label: '最終 CTA 説明文',
          action: 'updated',
          scope: 'section',
          pageId: page.id,
          sectionId: ctaSection.id,
          before: previousBody,
          after: ctaSection.body,
        }),
      );
    }

    return siteChangeProposalSchema.parse({
      nextDocument,
      summary: '全体のトーンを保ちつつ、文章を短くシンプルに整えました。',
      diff,
    });
  }

  if (/(信頼|先進|高級|ポップ|やわらか|フォーマル|トーン)/iu.test(lowerPrompt)) {
    let preset = document.theme.preset;
    if (normalizedPrompt.includes('先進')) preset = '先進的';
    else if (normalizedPrompt.includes('高級')) preset = '高級感';
    else if (normalizedPrompt.includes('ポップ')) preset = 'ポップ';
    else if (normalizedPrompt.includes('やわらか')) preset = 'やわらかめ';
    else if (normalizedPrompt.includes('フォーマル')) preset = 'フォーマル';
    else if (normalizedPrompt.includes('シンプル')) preset = 'シンプル';
    else preset = '信頼';

    const previousPreset = nextDocument.theme.preset;
    const previousPrimary = nextDocument.theme.tokens.primary;
    nextDocument.theme.preset = preset;
    nextDocument.theme.tokens = buildTokens(preset);
    diff.theme.push(
      createDiffEntry({
        label: 'テーマ preset',
        action: 'updated',
        scope: 'site',
        before: previousPreset,
        after: preset,
      }),
    );
    diff.theme.push(
      createDiffEntry({
        label: 'primary カラー',
        action: 'updated',
        scope: 'site',
        before: previousPrimary,
        after: nextDocument.theme.tokens.primary,
      }),
    );

    return siteChangeProposalSchema.parse({
      nextDocument,
      summary: `テーマを「${preset}」寄りに調整しました。`,
      diff,
    });
  }

  const heroSection = getHeroSection(page);
  if (heroSection?.type === 'hero') {
    const previousSubhead = heroSection.subhead;
    heroSection.subhead = `${normalizedPrompt} を反映しやすいよう、導入文をわかりやすく整理しました。`;
    diff.text.push(
      createDiffEntry({
        label: 'Hero 補足文',
        action: 'updated',
        scope: 'section',
        pageId: page.id,
        sectionId: heroSection.id,
        before: previousSubhead,
        after: heroSection.subhead,
      }),
    );
  }

  return siteChangeProposalSchema.parse({
    nextDocument,
    summary: 'まずは導入文を調整し、次の編集につなげやすい下書きに整えました。',
    diff,
  });
}
